import { type DashboardReady } from "./useDashboardController";
import { Package, ShoppingBag, Star } from "lucide-react";
import { Link } from "wouter";
import { postHref } from "../../lib/postNavigation";
import { peso } from "./model";
import { OwnerPostMenu } from "../../components/OwnerPostMenu";

export function DashboardOrderActivity(props: DashboardReady) {
  const { myItems, isMobileViewport, showAllMyItems, refreshOrders, archiveItem, orderBusy, restoreItemAction, setShowAllMyItems, orders, showAllPurchases, orderAction, setDisputeDeal, pendingByDeal, setReviewDeal, setShowAllPurchases, openOrder } = props;

  const statusChip = (s: string) => {
    const map: Record<string, string> = {
      paid: "bg-navy-50 text-navy-600", fulfilled: "bg-brand-gold/15 text-[#8a5a00]",
      completed: "bg-brand-green/10 text-brand-greendark", cancelled: "bg-line text-mut",
    };
    return <span className={`chip capitalize ${map[s] || "bg-navy-50 text-navy-600"}`}>{s}</span>;
  };
  return (<>{/* My Items — Increment 11 */}
        {myItems && myItems.length > 0 && (
          <div id="sec-items" data-mobile-section={isMobileViewport ? "listings" : "work"} className="card p-5 mt-4 dashboard-section dashboard-items">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-navy" />
              <h3 className="font-bold text-navy">My Items for Sale / Rent</h3>
              <Link href={postHref("item", "/dashboard")} className="text-xs text-brand-red font-semibold ml-auto">+ List another</Link>
            </div>
            <div className="space-y-2">
              {(showAllMyItems ? myItems : myItems.slice(0, 5)).map((it) => (
                <div key={it.id} className="relative flex items-center justify-between border border-line rounded-lg p-3 gap-3">
                  <Link href={`/item/${it.id}`} className="text-sm text-navy min-w-0 hover:text-brand-red">
                    <span className="font-medium">{it.title}</span>
                    <span className="text-mut"> · {peso(it.price)}{it.mode === "rent" ? `/${it.unit || "day"}` : ""} · {it.stock} in stock</span>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <OwnerPostMenu kind="item" post={it} className="relative z-20" onChanged={refreshOrders} />
                    {it.status === "archived"
                      ? <span className="chip bg-line text-mut">Archived</span>
                      : <span className="chip bg-brand-green/10 text-brand-greendark">Active</span>}
                    {it.status === "active"
                      ? <button onClick={() => archiveItem(it.id)} disabled={orderBusy === it.id + "archive"} className="text-xs text-navy-600 hover:text-navy disabled:opacity-50">Archive</button>
                      : <button onClick={() => restoreItemAction(it.id)} disabled={orderBusy === it.id + "restore"} className="text-xs font-semibold text-brand-green disabled:opacity-50">Restore</button>}
                  </div>
                </div>
              ))}
            </div>
            {myItems.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={() => setShowAllMyItems((shown) => !shown)} aria-expanded={showAllMyItems} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{showAllMyItems ? "Show less" : "Show more"}</button></div>}
          </div>
        )}

        {/* My Orders — Increment 11 */}
        {orders && (orders.purchases.length > 0 || orders.sales.length > 0) && (
          <div data-mobile-section="orders" className="grid lg:grid-cols-2 gap-4 mt-4 dashboard-order-sections">
            {orders.purchases.length > 0 && (
              <div className={`card p-5 ${orders.sales.length === 0 ? "lg:col-span-2" : ""}`}>
                <div className="flex items-center gap-2 mb-3"><ShoppingBag className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">My Purchases</h3></div>
                <div className="space-y-2">
                  {(showAllPurchases ? orders.purchases : orders.purchases.slice(0, 5)).map((o) => (
                    <div key={o.id} className="border border-line rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <Link href={`/item/${o.itemId}`} className="text-sm font-medium text-navy hover:text-brand-red truncate">{o.itemTitle}</Link>
                        {statusChip(o.status)}
                      </div>
                      <div className="text-xs text-mut mt-1">{peso(o.total)} · {o.qty}× {o.mode === "rent" ? `${o.rentDays} day(s) rent` : "purchase"} · from {o.sellerName}</div>
                      {(o.status === "paid" || o.status === "fulfilled") && (
                        <div className="flex flex-wrap justify-end gap-2 mt-2">
                          <Link href={`/contracts?order=${encodeURIComponent(o.id)}`} className="btn-red h-8 px-3 text-xs">{(o.milestones?.length ?? 1) > 1 ? "View milestones" : "Confirm receipt"}</Link>
                          {o.mode === "rent" && <button onClick={() => orderAction(o.id, "cancel")} disabled={orderBusy === o.id + "cancel"} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Cancel</button>}
                          <button onClick={() => setDisputeDeal({ dealType: "order", dealId: o.id, contextLabel: o.itemTitle })} className="btn-outline h-8 px-3 text-xs text-brand-red disabled:opacity-50">Dispute</button>
                        </div>
                      )}
                      {o.status === "disputed" && <div className="mt-2 text-xs text-brand-red">Under dispute — an admin will resolve it.</div>}
                      {o.status === "completed" && pendingByDeal.has(o.id) && (
                        <div className="flex justify-end mt-2">
                          <button onClick={() => setReviewDeal(pendingByDeal.get(o.id))} className="btn-outline h-8 px-3 text-xs inline-flex"><Star className="w-3.5 h-3.5" />Leave a review</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {orders.purchases.length > 5 && <div className="mt-3 flex justify-end"><button type="button" onClick={() => setShowAllPurchases((shown) => !shown)} aria-expanded={showAllPurchases} className="text-sm font-semibold text-navy hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold">{showAllPurchases ? "Show less" : "Show more"}</button></div>}
              </div>
            )}
            {orders.sales.length > 0 && (
              <div className={`card p-5 ${orders.purchases.length === 0 ? "lg:col-span-2" : ""}`}>
                <div className="flex items-center gap-2 mb-3"><Package className="w-4 h-4 text-navy" /><h3 className="font-bold text-navy">My Sales</h3></div>
                <div className="space-y-2">
                  {orders.sales.map((o) => (
                    <div key={o.id} role="link" tabIndex={0}
                      onClick={(event) => {
                        const target = event.target;
                        if (target instanceof Element && target.closest("a,button")) return;
                        openOrder(o.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openOrder(o.id);
                        }
                      }}
                      className="group cursor-pointer border border-line rounded-lg p-3 transition-colors duration-150 hover:border-navy/30 hover:bg-navy-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/30">
                      <div className="flex items-center justify-between gap-2">
                        <Link href={`/item/${o.itemId}`} className="text-sm font-medium text-navy hover:text-brand-red truncate">{o.itemTitle}</Link>
                        {statusChip(o.status)}
                      </div>
                      <div className="text-xs text-mut mt-1">{peso(o.total)} · {o.qty}× {o.mode === "rent" ? `${o.rentDays} day(s) rent` : "sale"} · to {o.buyerName}</div>
                      <div className="flex flex-wrap justify-end gap-2 mt-2">
                        {o.status === "paid" && <button onClick={() => orderAction(o.id, "fulfill")} disabled={orderBusy === o.id + "fulfill"} className="btn-navy h-8 px-3 text-xs disabled:opacity-50">Mark fulfilled</button>}
                        {o.mode === "rent" && (o.status === "paid" || o.status === "fulfilled") && <button onClick={() => orderAction(o.id, "cancel")} disabled={orderBusy === o.id + "cancel"} className="btn-outline h-8 px-3 text-xs disabled:opacity-50">Cancel</button>}
                        {(o.status === "paid" || o.status === "fulfilled") && <button onClick={() => setDisputeDeal({ dealType: "order", dealId: o.id, contextLabel: o.itemTitle })} className="btn-outline h-8 px-3 text-xs text-brand-red">Dispute</button>}
                        {o.status === "disputed" && <span className="text-xs text-brand-red">Under dispute</span>}
                        {o.status === "completed" && <span className="text-xs text-brand-green">Paid out to your wallet</span>}
                        {o.status === "completed" && pendingByDeal.has(o.id) && <button onClick={() => setReviewDeal(pendingByDeal.get(o.id))} className="btn-outline h-8 px-3 text-xs inline-flex"><Star className="w-3.5 h-3.5" />Review buyer</button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        </>);
}
